import { useEffect, useState } from "react";
import { getTrafficStats } from "api";
import { formatNumberWithCommas } from "functions";
import VisitorsChart, {
  DEVICE_COLORS,
  DEVICE_LABELS,
  DeviceTooltipCard,
} from "./VisitorsChart";
import DevicePie from "./DevicePie";
import RoutesPie from "./RoutesPie";

const VIEW_LABELS = {
  today: "Today",
  week: "Week",
  month: "Month",
  year: "Year",
  allTime: "All Time",
};

// Small color-chip legend for the device palette. `keys` limits which
// devices are shown (e.g. hide "unknown" when there's no legacy data).
function DeviceLegend({ keys }) {
  return (
    <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
      {keys.map((key) => (
        <div
          key={key}
          style={{ display: "flex", alignItems: "center", gap: "7px", fontSize: "13px" }}
        >
          <span
            style={{
              display: "inline-block",
              width: 9,
              height: 9,
              borderRadius: 2,
              background: DEVICE_COLORS[key],
              flexShrink: 0,
            }}
          />
          <span style={{ opacity: 0.85 }}>{DEVICE_LABELS[key]}</span>
        </div>
      ))}
    </div>
  );
}

function TrafficDash() {
  const [view, setView] = useState("week");
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  // Cursor-following tooltip for the pages bars: { route, devices, x, y }.
  const [hoveredPage, setHoveredPage] = useState(null);

  useEffect(() => {
    getTrafficStats()
      .then(setStats)
      .catch((err) => {
        console.error("Failed to load traffic stats:", err);
        setError(true);
      });
  }, []);

  const current = stats?.[view];
  const maxPageViews = Math.max(1, ...(current?.pages || []).map((p) => p.views));
  const hasUnknown =
    (current?.devices?.unknown || 0) > 0 ||
    (current?.pages || []).some((p) => p.devices?.unknown > 0);
  const legendKeys = hasUnknown
    ? ["mobile", "desktop", "unknown"]
    : ["mobile", "desktop"];

  const summaryTile = (key) => {
    const isActive = view === key;
    return (
      <div
        key={key}
        onClick={() => setView(key)}
        className={`filter-button ${isActive ? "active-filter-button" : ""}`}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          padding: "14px 20px",
          fontSize: "22px",
          fontWeight: 600,
          border: "1px solid var(--ad-border)",
          borderRadius: "var(--ad-radius-sm)",
          background: isActive ? "rgba(211, 150, 58, 0.08)" : "transparent",
          borderColor: isActive ? "rgba(211, 150, 58, 0.35)" : "var(--ad-border)",
          color: isActive ? "var(--ad-gold)" : "var(--ad-text)",
        }}
      >
        <label className="res-count-label" style={{ pointerEvents: "none" }}>
          {VIEW_LABELS[key]}
        </label>
        <div style={{ pointerEvents: "none" }}>
          {stats ? formatNumberWithCommas(stats[key]?.visitors ?? 0) : "—"}
        </div>
        <div
          style={{
            pointerEvents: "none",
            fontSize: "12px",
            fontWeight: 500,
            opacity: 0.55,
            marginTop: -4,
          }}
        >
          unique visitors
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="dash-item dash-item-full faux-dash-item" style={{ padding: "24px" }}>
        <div style={{ opacity: 0.55, fontSize: "14px" }}>
          Couldn't load traffic data. Refresh to try again.
        </div>
      </div>
    );
  }

  return (
    <div
      className="dash-item dash-item-full"
      style={{
        background: "none",
        border: "none",
        padding: "0px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <div
        className="faux-dash-item"
        style={{ display: "flex", flexDirection: "row", gap: "12px", padding: "16px" }}
      >
        {["today", "week", "month", "year", "allTime"].map(summaryTile)}
      </div>

      {current?.series && (
        <div className="faux-dash-item breakdown">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: "4px",
            }}
          >
            <h2 style={{ height: "auto" }}>
              Unique Visitors per{" "}
              {{ hour: "Hour", day: "Day", month: "Month" }[current.granularity]} —{" "}
              {VIEW_LABELS[view]}
            </h2>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
              }}
            >
              <DeviceLegend keys={legendKeys} />
              <div style={{ fontSize: "13px", opacity: 0.7 }}>
                {formatNumberWithCommas(current.visitors)} visitors ·{" "}
                {formatNumberWithCommas(current.pageviews)} pageviews
              </div>
            </div>
          </div>
          <div className="pointer-events" style={{ height: "38vh" }}>
            <VisitorsChart series={current.series} granularity={current.granularity} />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
        {/* height: "auto" overrides .faux-dash-item's height: 100% so flex
            stretch works: the pie's content sets the row height and the
            pages panel stretches to match it exactly. */}
        <div
          className="faux-dash-item breakdown"
          style={{ flex: "0 0 260px", height: "auto" }}
        >
          <h2 style={{ height: "auto", marginBottom: "12px" }}>
            Devices — {VIEW_LABELS[view]}
          </h2>
          {!stats && <div style={{ opacity: 0.55, fontSize: "14px" }}>Loading…</div>}
          {stats && <DevicePie devices={current?.devices} />}
        </div>

        <div
          className="faux-dash-item breakdown"
          style={{ flex: "0 0 280px", height: "auto" }}
        >
          <h2 style={{ height: "auto", marginBottom: "12px" }}>
            Pages — {VIEW_LABELS[view]}
          </h2>
          {!stats && <div style={{ opacity: 0.55, fontSize: "14px" }}>Loading…</div>}
          {stats && (
            <RoutesPie pages={current?.pages} totalVisitors={current?.visitors} />
          )}
        </div>

        <div
          className="faux-dash-item breakdown"
          style={{
            flex: "1 1 480px",
            display: "flex",
            flexDirection: "column",
            height: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: "12px",
            }}
          >
            <h2 style={{ height: "auto" }}>Most Visited Pages — {VIEW_LABELS[view]}</h2>
            <DeviceLegend keys={legendKeys} />
          </div>
          {!stats && <div style={{ opacity: 0.55, fontSize: "14px" }}>Loading…</div>}
          {stats && current?.pages?.length === 0 && (
            <div style={{ opacity: 0.55, fontSize: "14px" }}>
              No traffic in this range yet.
            </div>
          )}
          {/* flex-basis 0 keeps the list from contributing to the row's
              height — it fills (or scrolls within) whatever height the pie
              panel sets. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              flex: "1 1 0",
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {(current?.pages || []).map((page, i) => (
              // flex-grow spreads the rows over the panel's full height so
              // a short list fills the widget instead of bunching at the top.
              <div
                key={page.route}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  fontSize: "14px",
                  flex: "1 0 auto",
                }}
              >
                <span style={{ width: 22, opacity: 0.45, textAlign: "right", flexShrink: 0 }}>
                  {i + 1}
                </span>
                <span style={{ width: 110, flexShrink: 0 }}>{page.route}</span>
                {/* alignSelf: stretch — the bar fills the row's height, so
                    bars get thicker as the rows spread to fill the panel. */}
                <div
                  style={{
                    flex: 1,
                    alignSelf: "stretch",
                    margin: "5px 0",
                    minHeight: 10,
                    borderRadius: 7,
                    background: "rgba(255,255,255,0.05)",
                    overflow: "hidden",
                    display: "flex",
                  }}
                  onMouseMove={(e) =>
                    setHoveredPage({
                      route: page.route,
                      devices: page.devices,
                      x: e.clientX,
                      y: e.clientY,
                    })
                  }
                  onMouseLeave={() => setHoveredPage(null)}
                >
                  {["mobile", "desktop", "unknown"].map(
                    (d) =>
                      page.devices?.[d] > 0 && (
                        <div
                          key={d}
                          style={{
                            width: `${(page.devices[d] / maxPageViews) * 100}%`,
                            height: "100%",
                            background: DEVICE_COLORS[d],
                          }}
                        />
                      ),
                  )}
                </div>
                <span style={{ width: 110, textAlign: "right", flexShrink: 0 }}>
                  <span style={{ fontWeight: 700 }}>{formatNumberWithCommas(page.views)}</span>{" "}
                  <span style={{ opacity: 0.55, fontSize: "12px" }}>views</span>
                </span>
                <span style={{ width: 110, textAlign: "right", flexShrink: 0 }}>
                  <span style={{ fontWeight: 700 }}>
                    {formatNumberWithCommas(page.visitors)}
                  </span>{" "}
                  <span style={{ opacity: 0.55, fontSize: "12px" }}>visitors</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* position: fixed escapes the list's overflow clipping, so the
          tooltip never gets cut off by the scroll container. */}
      {hoveredPage && (
        <div
          style={{
            position: "fixed",
            left: hoveredPage.x + 14,
            top: hoveredPage.y + 14,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <DeviceTooltipCard
            label={hoveredPage.route}
            keys={legendKeys}
            values={hoveredPage.devices}
          />
        </div>
      )}
    </div>
  );
}

export default TrafficDash;
